import React, { useEffect, useState } from 'react';
import { SessionCheck } from '@parsina/framework';
import { ShieldAlert } from 'lucide-react';

import { useTranslation } from '../i18n';
import {
  clearRedirectGuard, clearTokens, getRefresh, getToken, handleCallback, isTokenExpired, logout,
  redirectToLogin, refreshAccessToken,
} from '../lib/auth';
import { KC_BASE_URL } from '../lib/runtimeEnv';
import { FORBIDDEN_EVENT, markAppMounted } from '../lib/api';
import { takeReturnUrl } from './SessionDialog';
import SystemState from './SystemState';
import { brandAsset } from '../lib/brandAssets';

/** The Keycloak role this panel requires. An identifier, so never translated. */
const REQUIRED_ROLE = 'platform_admin';

/**
 * Sign-in gate for the back office.
 *
 * Without this the panel rendered its shell and then failed every query with
 * "Authentication required" — nothing ever put a token anywhere.
 *
 * Two states are deliberately kept apart:
 *
 *   NOT SIGNED IN  → redirect to Keycloak. Nothing to show, nothing to decide,
 *     and boot is the ONE place a redirect is right (§13.1) because there is
 *     nothing on screen to lose.
 *   SIGNED IN, NO ROLE → a message, NOT a redirect. The operator is
 *     authenticated; logging in again produces the same token and the same
 *     rejection, so a redirect here is an infinite loop that looks like a
 *     broken site.
 */
export default function AuthGate({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState('checking');   // checking | ready | denied | looped

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const params = new URLSearchParams(window.location.search);

      // The redirect guard gave up on an earlier pass. Rendering an error is the
      // ONLY thing that breaks the cycle — this branch used to be missing, so the
      // guard reloaded onto ?auth_error=loop, found no token, redirected, tripped
      // the guard again and reloaded forever on a blank page. Check it first,
      // before anything that could redirect.
      if (params.get('auth_error')) {
        setState('looped');
        return;
      }

      // Returning from Keycloak with ?code=… — exchange it before anything else.
      // Same shape the doctor and clinic panels use, and for the same reason: a
      // failed exchange is usually a single-use code being replayed (a refresh,
      // a back button, a stale tab), and one clean login fixes it. Dead-ending
      // here instead traps the operator on an error screen that a re-login would
      // have cleared. Runaway retries are the redirect guard's job, and the
      // auth_error branch above is what finally surfaces them.
      if (params.has('code')) {
        const exchanged = await handleCallback();
        if (cancelled) return;
        if (!exchanged) { await redirectToLogin(); return; }
        // Back to where the session ended, not to the dashboard: an operator who
        // was three filters deep in the estate when their token expired should
        // not have to find their way back.
        const back = takeReturnUrl();
        if (back && back !== '/') window.history.replaceState({}, '', back);
      }

      const token = getToken();
      // Silent sign-in from the stored 30-day refresh token first (PM-0158).
      const restored = (!token || isTokenExpired(token)) && getRefresh() ? await refreshAccessToken() : false;
      if (!restored && (!token || isTokenExpired(token))) {
        await redirectToLogin();
        return;
      }
      if (!cancelled) {
        // From here on an expiry is an EVENT over the live screen rather than a
        // navigation that would discard an open form (§13.1).
        markAppMounted();
        setState('ready');
      }
    })().catch(() => {
      if (!cancelled) setState('denied');
    });

    return () => { cancelled = true; };
  }, []);

  // A FORBIDDEN anywhere in the app means "signed in, but not platform staff".
  // Surfaced here so every screen does not have to handle it separately.
  useEffect(() => {
    const onForbidden = () => setState('denied');
    window.addEventListener(FORBIDDEN_EVENT, onForbidden);
    return () => window.removeEventListener(FORBIDDEN_EVENT, onForbidden);
  }, []);

  // The kit's session card (packages/DESIGN.md §"Session check") — the same
  // card, motion and manual sign-in link as every other Parsina panel.
  if (state === 'checking') {
    return (
      <SessionCheck
        logo={brandAsset('logoWhite')}
        brandName={t('common.brand.name')}
        panelName={t('nav.product')}
        title={t('auth.checking')}
        subtitle={t('auth.checkingHint')}
        stuckLabel={t('auth.stuck')}
        manualLabel={t('auth.manualSignIn')}
        onManualSignIn={() => { redirectToLogin(); }}
      />
    );
  }

  // Signed in against a Keycloak the BFF does not trust, or an exchange that
  // keeps failing. The SSO origin is shown because the failure this screen was
  // written for was a stale bundle pointing at the retired SSO host: the login
  // succeeded there, and the BFF rejected the resulting token forever.
  if (state === 'looped') {
    return (
      <SystemState
        tone="warning"
        icon={<ShieldAlert size={30} aria-hidden />}
        titleKey="auth.loop.title"
        adviceKey="auth.loop.body"
        detail={KC_BASE_URL.replace(/^https?:\/\//, '')}
        fullscreen
        testId="auth-looped"
        actions={[
          {
            key: 'retry',
            label: t('auth.loop.retry'),
            testId: 'auth-looped-retry',
            onClick: () => {
              clearRedirectGuard();
              clearTokens();
              window.location.href = '/';
            },
          },
          {
            key: 'other',
            label: t('auth.loop.other'),
            variant: 'secondary',
            testId: 'auth-looped-logout',
            onClick: () => { clearRedirectGuard(); clearTokens(); logout(); },
          },
        ]}
      />
    );
  }

  if (state === 'denied') {
    return (
      <SystemState
        tone="neutral"
        code="403"
        icon={<ShieldAlert size={30} aria-hidden />}
        titleKey="auth.denied.title"
        adviceKey="auth.denied.body"
        // The role name as its own LTR line: it is the actionable half, and an
        // identifier inside a Persian sentence is at bidi's mercy.
        detail={REQUIRED_ROLE}
        fullscreen
        testId="auth-denied"
        actions={[{
          key: 'other',
          label: t('auth.denied.action'),
          variant: 'secondary',
          testId: 'auth-denied-logout',
          onClick: () => { clearTokens(); logout(); },
        }]}
      />
    );
  }

  return children;
}
